import { useCallback } from "react";
import { User } from "@/src/types/user";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Button } from "@/src/components/ui/button";
import { TrophyIcon } from "lucide-react";
import { useConfirmDialog } from "@/src/components/confirmDialog";
import useAdmin from "@/src/hooks/useAdmin";
import useSuperAdmin from "@/src/hooks/useSuperAdmin";

interface UserDetailsProps {
  user: User;
  onAddCoinsClick: () => void;
  onRewardHistoryClick: () => void;
  onVerifyLivenessClick: () => void;
  isVerifyingLiveness: boolean;
}

export function UserDetails({
  user,
  onAddCoinsClick,
  onRewardHistoryClick,
  onVerifyLivenessClick,
  isVerifyingLiveness,
}: UserDetailsProps) {
  const isAdmin = useAdmin();
  const isSuperAdmin = useSuperAdmin();
  const canVerifyLiveness = isAdmin || isSuperAdmin;

  const { openDialog, ConfirmDialog } = useConfirmDialog({
    title: "Verify Liveness",
    description:
      "Are you sure you want to verify the liveness of this user? This action is irreversible.",
  });

  const handleVerifyLivenessClick = useCallback(async () => {
    const result = await openDialog();
    if (!result) return;

    onVerifyLivenessClick();
  }, [onVerifyLivenessClick, openDialog]);

  return (
    <Card className="mb-4">
      <CardHeader className="flex-row justify-between items-center">
        <CardTitle>User Information</CardTitle>
        <div className="flex flex-row gap-2">
          <Button size="sm" variant="ghost" onClick={onRewardHistoryClick}>
            <TrophyIcon className="mr-2 size-5" />
            <span>Reward History</span>
          </Button>
          <Button size="sm" onClick={onAddCoinsClick}>
            Add Coins
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-semibold">User ID:</p>
            <p>{user.userId}</p>
          </div>
          <div>
            <p className="font-semibold">Platform:</p>
            <p>{user.platformId === 1 ? "Android" : "iOS"}</p>
          </div>
          <div>
            <p className="font-semibold">Device ID:</p>
            <p>{user.deviceId}</p>
          </div>
          <div>
            <p className="font-semibold">Device Type:</p>
            <p>{user.deviceType}</p>
          </div>
          <div>
            <p className="font-semibold">Device Rooted:</p>
            <p>{user.isDeviceRooted ? "Yes" : "No"}</p>
          </div>
          <div>
            <p className="font-semibold">Country Code:</p>
            <p>{user.countryCode}</p>
          </div>
          <div>
            <p className="font-semibold">Liveness Verified:</p>
            <div className="flex flex-row gap-2">
              <p>{user.isLivenessVerified ? "Yes" : "No"}</p>
              {canVerifyLiveness && !user.isLivenessVerified && (
                <Button
                  size="xs"
                  onClick={handleVerifyLivenessClick}
                  disabled={isVerifyingLiveness}
                >
                  {isVerifyingLiveness ? "Verifying..." : "Verify Liveness"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      {ConfirmDialog}
    </Card>
  );
}
